import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useNavigate } from "react-router-dom";

import { Container } from "@magnetic/container";
import { Divider } from "@magnetic/divider";
import { Code, PaintBrush, User } from "@magnetic/icons";
import { Text } from "@magnetic/text";
import { createPortal } from "react-dom";

import { Button } from "src/components/buttons";
import { HFlex } from "src/components/hFlex";
import { ThemeToggle } from "src/components/themeToggle";
import { VFlex } from "src/components/vFlex";
import { LoginContext } from "src/core/loginContext";
import { UserRoleType } from "src/dataTypes/userRoleType";
import { TimeZoneSection } from "src/pages/timezone/TimeZoneSection";
import { Constants } from "src/utils/constants";
import { getApplicationTokenRoute } from "src/utils/routesUtils";

type UserProfileMenuItem = {
  readonly id: string;
  readonly hide?: boolean;
  readonly icon?: React.ReactElement;
  readonly content: React.ReactElement;
};

type UserProfileMenuProps = {
  readonly dropdownItems: readonly UserProfileMenuItem[];
};

const MENU_ITEM_ICON_SIZE = 24;

const UserProfileMenu = ({ dropdownItems }: UserProfileMenuProps) => {
  return (
    <Container className="user-profile app-header-user-profile">
      <VFlex className="user-profile-actions" gap="none">
        {dropdownItems
          .filter(({ hide }) => !hide)
          .map(({ id, content, icon }, index, filteredItems) => (
            <div key={id}>
              <HFlex className="dropdown-item" align="center" gap="none">
                {icon && <div className="icon-container">{icon}</div>}
                <div className="content-container">{content}</div>
              </HFlex>
              {index !== filteredItems.length - 1 && <Divider />}
            </div>
          ))}
      </VFlex>
    </Container>
  );
};

export const UserProfile = () => {
  const { session, isReadOnly, logout } = useContext(LoginContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDropdownOpen) {
      return undefined;
    }

    const handleMouseDown = (event: MouseEvent): void => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      // Keep the profile menu open when interacting with Magnetic's portaled timezone select.
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target) ||
        document.getElementById("timezone-select")?.contains(target)
      ) {
        return;
      }

      setIsDropdownOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDropdownOpen]);

  const handleClick = useCallback(() => {
    setIsDropdownOpen((currentValue) => !currentValue);
  }, []);

  const dropdownItems: UserProfileMenuItem[] = [
    {
      id: "loggedInAs",
      icon: <User size={MENU_ITEM_ICON_SIZE} />,
      content: (
        <HFlex align="flex-start" justify="space-between">
          <VFlex align="flex-start" gap="xxs">
            <Text>LOGGED IN AS</Text>
            <VFlex align="flex-start" data-qa="profile-user-details" gap="none">
              <Text data-qa="profile-user-email" weight="bold">
                {session?.email || Constants.EMPTY_VALUE}
              </Text>
              <Text data-qa="profile-organization-name">
                {session?.tenantName || Constants.EMPTY_VALUE}
              </Text>
              <Text data-qa="profile-user-role">
                {session?.role && new UserRoleType(session.role).toString()}
              </Text>
            </VFlex>
          </VFlex>
          <Button
            name="logout"
            className="logout"
            kind="secondary"
            onClick={logout}
          >
            Log out
          </Button>
        </HFlex>
      )
    },
    {
      id: "themeSwitch",
      icon: (
        <PaintBrush weight="bold" size={MENU_ITEM_ICON_SIZE} className="icon" />
      ),
      content: <ThemeToggle />
    },
    {
      id: "timeZoneSelector",
      content: <TimeZoneSection />
    },
    {
      id: "tokensLink",
      icon: <Code weight="bold" size={MENU_ITEM_ICON_SIZE} className="icon" />,
      hide: isReadOnly,
      content: (
        <Button
          name="apiBearerTokens"
          className="api-bearer-tokens"
          kind="tertiary"
          onClick={() => {
            setIsDropdownOpen(false);
            navigate(getApplicationTokenRoute());
          }}
        >
          Generate tokens
        </Button>
      )
    }
  ];

  return (
    <>
      <button
        aria-expanded={isDropdownOpen}
        aria-label="User profile"
        className="mds-header-utility mds-header-profile-and-tenant"
        onClick={handleClick}
        ref={buttonRef}
        type="button"
      >
        <span>
          <User size={MENU_ITEM_ICON_SIZE} weight="bold" />
        </span>
      </button>
      {isDropdownOpen &&
        createPortal(
          <div className="app-header-profile-floating-panel" ref={panelRef}>
            <div
              className="mds-header-dropdown-panel app-header-profile-panel"
              role="dialog"
            >
              <UserProfileMenu dropdownItems={dropdownItems} />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
